import { useState, useEffect } from 'react';
import { listOrgCollection, readOrgFile } from '../../storage/FileSystemAdapter';
import { MS_PER_DAY } from '../../utils/constants';
import { TEXT_MUTED, TEXT_DISABLED, BORDER_DEFAULT, TEXT_HEADING, BRAND_PRIMARY, ERROR_BG, ERROR_BORDER, BG_SHELL, FIELD_ERROR_BORDER, ERROR_TEXT, SURFACE_WHITE, FONT_SECTION_TITLE, FONT_BODY_STRONG, FONT_LABEL, FONT_SMALL, FONT_TINY, FONT_FAMILY_DISPLAY } from '../../theme';

// ─── PRINT STYLES ─────────────────────────────────────────────────────────────
const PRINT_STYLES = `
  @media print {
    .exec-no-print { display: none !important; }
    .exec-root { background: white !important; padding: 1rem !important; }
    .exec-header {
      background: white !important;
      border: 1px solid #999 !important;
      box-shadow: none !important;
    }
    .exec-panel {
      background: white !important;
      border: 1px solid #999 !important;
      box-shadow: none !important;
      break-inside: avoid;
      page-break-inside: avoid;
    }
    .exec-badge { background: white !important; border-color: #999 !important; }
    .exec-score-bar { display: none !important; }
    .exec-grid {
      break-inside: avoid;
      page-break-inside: avoid;
    }
  }
`;

// ─── I18N ─────────────────────────────────────────────────────────────────────
const T = {
  es: {
    loading: 'Cargando…',
    reportTitle: 'Informe de gestión del SGSI',
    reportDate: 'Fecha del informe',
    exportPdf: 'Exportar a PDF',
    certStatusTitle: 'Estado de certificación',
    nextMilestoneTitle: 'Próximo hito',
    certStatusLabels: {
      none: 'Sin certificación', preparing: 'Preparando certificación',
      stage1: 'Fase 1', stage2: 'Fase 2', certified: 'Certificado',
      surveillance: 'Seguimiento', expired: 'Vencido',
    },
    certDate: 'Fecha de certificación',
    certExpired: 'Vencida',
    maturityTitle: 'Madurez del SGSI',
    outOf: '/100',
    trendStable: '→ estable',
    riskPostureTitle: 'Postura de riesgo',
    high: 'Alto', medium: 'Medio', low: 'Bajo',
    treatedOrAccepted: 'riesgos tratados o aceptados',
    noRisks: 'Sin riesgos registrados',
    complianceTitle: 'Cobertura de cumplimiento',
    controlsImplemented: 'Controles implementados',
    objectivesMet: 'Objetivos cumplidos',
    ofControls: 'controles',
    ofObjectives: 'objetivos',
    calendarTitle: 'Calendario de auditorías y revisiones',
    lastInternalAudit: 'Última auditoría interna',
    nextPlannedAudit: 'Próxima auditoría planificada',
    lastMgmtReview: 'Última revisión por la dirección',
    nextMgmtReview: 'Próxima revisión programada',
    notRecorded: 'No registrada',
    notScheduled: 'No programada',
    ncTitle: 'No conformidades',
    openNcs: 'Abiertas',
    closedNcs: 'Cerradas',
    closureRate: 'Tasa de cierre',
    noNcs: 'Sin no conformidades registradas',
  },
  en: {
    loading: 'Loading…',
    reportTitle: 'ISMS Management Report',
    reportDate: 'Report date',
    exportPdf: 'Export to PDF',
    certStatusTitle: 'Certification status',
    nextMilestoneTitle: 'Next milestone',
    certStatusLabels: {
      none: 'Not certified', preparing: 'Preparing',
      stage1: 'Stage 1', stage2: 'Stage 2', certified: 'Certified',
      surveillance: 'Surveillance', expired: 'Expired',
    },
    certDate: 'Certification date',
    certExpired: 'Expired',
    maturityTitle: 'ISMS Maturity Score',
    outOf: '/100',
    trendStable: '→ stable',
    riskPostureTitle: 'Risk posture',
    high: 'High', medium: 'Medium', low: 'Low',
    treatedOrAccepted: 'risks treated or accepted',
    noRisks: 'No risks recorded',
    complianceTitle: 'Compliance coverage',
    controlsImplemented: 'Controls implemented',
    objectivesMet: 'Objectives met',
    ofControls: 'controls',
    ofObjectives: 'objectives',
    calendarTitle: 'Audit and review calendar',
    lastInternalAudit: 'Last internal audit',
    nextPlannedAudit: 'Next planned audit',
    lastMgmtReview: 'Last management review',
    nextMgmtReview: 'Next scheduled review',
    notRecorded: 'Not recorded',
    notScheduled: 'Not scheduled',
    ncTitle: 'Nonconformities',
    openNcs: 'Open',
    closedNcs: 'Closed',
    closureRate: 'Closure rate',
    noNcs: 'No nonconformities recorded',
  },
};

// ─── HELPERS ──────────────────────────────────────────────────────────────────
function fmtDate(dateStr, lang) {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString(lang === 'es' ? 'es-ES' : 'en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  } catch { return dateStr; }
}

function computeTrend(history) {
  if (!Array.isArray(history) || history.length < 2) return null;
  const thirtyDaysAgo = new Date(Date.now() - 30 * MS_PER_DAY);
  const baseline = [...history].reverse().find(e => new Date(e.timestamp) <= thirtyDaysAgo);
  if (!baseline) return null;
  return Math.round(history[history.length - 1].score - baseline.score);
}

function getNextMilestone(org) {
  const cs = org.certificationStatus;
  if (cs === 'preparing' && org.stage1AuditDate)
    return { label: { es: 'Auditoría Fase 1', en: 'Stage 1 audit' }, date: org.stage1AuditDate };
  if ((cs === 'stage1' || cs === 'stage2') && org.stage2AuditDate)
    return { label: { es: 'Auditoría Fase 2', en: 'Stage 2 audit' }, date: org.stage2AuditDate };
  if ((cs === 'certified' || cs === 'surveillance') && org.recertificationDate)
    return { label: { es: 'Recertificación', en: 'Recertification' }, date: org.recertificationDate };
  return null;
}

function scoreColor(score) {
  if (score >= 70) return BRAND_PRIMARY;
  if (score >= 40) return '#d97706';
  return FIELD_ERROR_BORDER; // TODO: P6b — verify color role
}

// ─── COMPONENT ────────────────────────────────────────────────────────────────
// eslint-disable-next-line no-unused-vars
export default function ExecutiveDashboardView({ org, onBack }) {
  const orgId = org.id;
  const orgLang = org.language || 'en';
  const t = T[orgLang] || T.en;

  const [loading, setLoading] = useState(true);
  const [risks, setRisks] = useState(null);
  const [soa, setSoa] = useState(null);
  const [objectives, setObjectives] = useState(null);
  const [audits, setAudits] = useState(null);
  const [nonconformities, setNonconformities] = useState(null);
  const [mgmtReviews, setMgmtReviews] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      const [pRisks, pSoa, pObjectives, pAudits, pNcs, pMrs] = await Promise.all([
        listOrgCollection(orgId, 'risks').catch(() => null),
        readOrgFile(orgId, 'controls/soa.json').catch(() => null),
        listOrgCollection(orgId, 'objectives').catch(() => null),
        listOrgCollection(orgId, 'audits').catch(() => null),
        listOrgCollection(orgId, 'nonconformities').catch(() => null),
        listOrgCollection(orgId, 'management-reviews').catch(() => null),
      ]);
      if (cancelled) return;
      setRisks(pRisks);
      setSoa(pSoa);
      setObjectives(pObjectives);
      setAudits(pAudits);
      setNonconformities(pNcs);
      setMgmtReviews(pMrs);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [orgId]);

  // ── Derived: today's date ─────────────────────────────────────────────────
  const todayStr = new Date().toLocaleDateString(
    orgLang === 'es' ? 'es-ES' : 'en-GB',
    { day: '2-digit', month: 'long', year: 'numeric' }
  );

  // ── Panel 1: Certification ────────────────────────────────────────────────
  const certStatus = org.certificationStatus || 'none';
  const certLabel = (t.certStatusLabels && t.certStatusLabels[certStatus]) || certStatus;
  const recertExpired = !!(org.recertificationDate && new Date(org.recertificationDate) < new Date());
  const nextMilestone = getNextMilestone(org);

  // ── Panel 2: Maturity score ───────────────────────────────────────────────
  const score = org.healthScore != null ? org.healthScore : null;
  const trend = computeTrend(org.healthScoreHistory);

  // ── Panel 3: Risk posture ─────────────────────────────────────────────────
  const riskHigh    = risks ? risks.filter(r => r.riskLevel === 'HIGH').length   : null;
  const riskMedium  = risks ? risks.filter(r => r.riskLevel === 'MEDIUM').length : null;
  const riskLow     = risks ? risks.filter(r => r.riskLevel === 'LOW').length    : null;
  const riskTotal   = risks ? risks.length : null;
  const riskTreated = risks
    ? risks.filter(r => r.treatmentActionStatus === 'Completed' || r.accepted === true).length
    : null;

  // ── Panel 4: Compliance coverage ─────────────────────────────────────────
  const soaApplicable  = soa ? (soa.controls || []).filter(c => c.applicable) : null;
  const soaTotal       = soaApplicable ? soaApplicable.length : null;
  const soaImplemented = soaApplicable
    ? soaApplicable.filter(c => c.implementationStatus === 'Implemented').length
    : null;
  const controlsPct = soaTotal > 0 ? Math.round((soaImplemented / soaTotal) * 100) : null;

  const objTotal       = objectives ? objectives.length : null;
  const objMet         = objectives
    ? objectives.filter(o => o.status === 'Achieved' || o.status === 'On Track').length
    : null;
  const objectivesPct  = objTotal > 0 ? Math.round((objMet / objTotal) * 100) : null;

  // ── Panel 5: Audit calendar ───────────────────────────────────────────────
  const completedInternalAudits = audits
    ? audits
        .filter(a => a.type === 'Internal' && a.status === 'Completed' && (a.actualDate || a.plannedDate))
        .sort((a, b) => new Date(b.actualDate || b.plannedDate) - new Date(a.actualDate || a.plannedDate))
    : null;
  const lastAuditDate = completedInternalAudits && completedInternalAudits.length > 0
    ? (completedInternalAudits[0].actualDate || completedInternalAudits[0].plannedDate)
    : null;
  const nextPlannedAuditItem = audits
    ? audits
        .filter(a => (a.status === 'Planned' || a.status === 'In Progress') && a.plannedDate)
        .sort((a, b) => new Date(a.plannedDate) - new Date(b.plannedDate))[0] || null
    : null;

  const completedMrs = mgmtReviews
    ? mgmtReviews
        .filter(mr => mr.approvalDate && mr.date)
        .sort((a, b) => new Date(b.date) - new Date(a.date))
    : null;
  const lastMrDate = completedMrs && completedMrs.length > 0 ? completedMrs[0].date : null;

  const mostRecentMr = mgmtReviews
    ? [...mgmtReviews]
        .filter(mr => mr.date)
        .sort((a, b) => new Date(b.date) - new Date(a.date))[0] || null
    : null;
  const nextMrDate = mostRecentMr ? (mostRecentMr.nextMeetingDate || null) : null;

  // ── Panel 6: NCs ─────────────────────────────────────────────────────────
  const ncOpen   = nonconformities ? nonconformities.filter(nc => nc.status !== 'Closed').length : null;
  const ncClosed = nonconformities ? nonconformities.filter(nc => nc.status === 'Closed').length : null;
  const ncTotal  = nonconformities ? nonconformities.length : null;
  const ncRate   = ncTotal > 0 ? Math.round((ncClosed / ncTotal) * 100) : null;

  // ── Shared styles ─────────────────────────────────────────────────────────
  const panel = {
    background: SURFACE_WHITE, border: `1px solid ${BORDER_DEFAULT}`,
    borderRadius: 10, padding: '1.5rem', boxSizing: 'border-box',
  };
  const panelLabel = {
    ...FONT_TINY, color: TEXT_MUTED,
    letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '1rem',
  };
  const subLabel = {
    ...FONT_TINY, color: TEXT_DISABLED,
    textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.2rem',
  };
  const valueLg = {
    ...FONT_BODY_STRONG, color: TEXT_HEADING,
  };
  const dash = (
    <span style={{ ...FONT_LABEL, color: TEXT_DISABLED }}>—</span>
  );

  // ── Loading screen ────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="exec-root" style={{ minHeight: '100vh', background: BG_SHELL, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <style>{PRINT_STYLES}</style>
        <span style={{ ...FONT_LABEL, color: TEXT_MUTED }}>
          {t.loading}
        </span>
      </div>
    );
  }

  return (
    <div className="exec-root" style={{ minHeight: '100vh', background: BG_SHELL, padding: '2rem 1.5rem', boxSizing: 'border-box' }}>
      <style>{PRINT_STYLES}</style>

      {/* ── Header ────────────────────────────────────────────────────────── */}
      <div
        className="exec-header"
        style={{
          maxWidth: 1200, margin: '0 auto 1.5rem',
          background: SURFACE_WHITE, border: `1px solid ${BORDER_DEFAULT}`, borderRadius: 10,
          padding: '1.5rem 2rem', display: 'flex',
          alignItems: 'flex-start', justifyContent: 'space-between',
          flexWrap: 'wrap', gap: '1rem', boxSizing: 'border-box',
        }}
      >
        <div>
          <h1 style={{ fontFamily: FONT_FAMILY_DISPLAY, fontWeight: 800, fontSize: '1.6rem', color: TEXT_HEADING, margin: '0 0 0.3rem', lineHeight: 1.2 }}>
            {org.name}
          </h1>
          <p style={{ ...FONT_LABEL, color: TEXT_MUTED, margin: 0 }}>
            {t.reportTitle}
          </p>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.75rem' }}>
          <span style={{ ...FONT_SMALL, color: TEXT_DISABLED }}>
            {t.reportDate}: {todayStr}
          </span>
          <div className="exec-no-print" style={{ display: 'flex', gap: '0.6rem' }}>
            <button
              onClick={() => window.print()}
              style={{
                ...FONT_SMALL,
                background: TEXT_HEADING, border: 'none', borderRadius: 6,
                padding: '0.35rem 0.85rem', cursor: 'pointer',
                color: SURFACE_WHITE,
              }}
            >
              {t.exportPdf}
            </button>
          </div>
        </div>
      </div>

      {/* ── Panel grid ────────────────────────────────────────────────────── */}
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>

        {/* Row 1: Panel 1 + Panel 2 */}
        <div className="exec-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem', marginBottom: '1.25rem' }}>

          {/* Panel 1 — Certification status */}
          <div className="exec-panel" style={panel}>
            <div style={panelLabel}>{t.certStatusTitle}</div>
            <div style={{ ...FONT_SECTION_TITLE, color: TEXT_HEADING, marginBottom: org.certificationDate ? '0.6rem' : '0' }}>
              {certLabel}
            </div>
            {org.certificationDate && (
              <div style={{ ...FONT_LABEL, color: TEXT_MUTED, marginBottom: '0.5rem' }}>
                {t.certDate}: {fmtDate(org.certificationDate, orgLang)}
              </div>
            )}
            {recertExpired ? (
              <div style={{ ...FONT_LABEL, color: FIELD_ERROR_BORDER /* TODO: P6b — verify color role */, marginTop: '0.5rem' }}>
                {t.certExpired}
              </div>
            ) : nextMilestone ? (
              <div style={{ marginTop: '1rem', paddingTop: '0.75rem', borderTop: '1px solid #e5e3df' }}>
                <div style={subLabel}>{t.nextMilestoneTitle}</div>
                <div style={valueLg}>
                  {nextMilestone.label[orgLang]} — {fmtDate(nextMilestone.date, orgLang)}
                </div>
              </div>
            ) : null}
          </div>

          {/* Panel 2 — ISMS Maturity Score */}
          <div className="exec-panel" style={panel}>
            <div style={panelLabel}>{t.maturityTitle}</div>
            {score !== null ? (
              <>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem', marginBottom: '0.75rem' }}>
                  <span style={{ fontFamily: FONT_FAMILY_DISPLAY, fontWeight: 800, fontSize: '3rem', color: scoreColor(score), lineHeight: 1 }}>
                    {score}
                  </span>
                  <span style={{ ...FONT_LABEL, color: TEXT_DISABLED }}>
                    {t.outOf}
                  </span>
                  {trend !== null ? (
                    <span style={{ ...FONT_LABEL, color: trend > 0 ? BRAND_PRIMARY : trend < 0 ? FIELD_ERROR_BORDER /* TODO: P6b — verify color role */ : TEXT_DISABLED }}>
                      {trend > 0 ? `↑ +${trend}` : trend < 0 ? `↓ ${trend}` : t.trendStable}
                    </span>
                  ) : (
                    <span style={{ ...FONT_LABEL, color: TEXT_DISABLED }}>—</span>
                  )}
                </div>
                <div className="exec-score-bar" style={{ height: 8, background: '#e5e3df', borderRadius: 4, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${score}%`, background: scoreColor(score), borderRadius: 4 }} />
                </div>
              </>
            ) : dash}
          </div>

        </div>

        {/* Row 2: Panel 3 + Panel 4 */}
        <div className="exec-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem', marginBottom: '1.25rem' }}>

          {/* Panel 3 — Risk posture */}
          <div className="exec-panel" style={panel}>
            <div style={panelLabel}>{t.riskPostureTitle}</div>
            {risks === null ? dash
              : riskTotal === 0 ? (
                <span style={{ ...FONT_LABEL, color: TEXT_DISABLED }}>{t.noRisks}</span>
              ) : (
                <>
                  <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                    {[
                      { label: t.high,   count: riskHigh,   color: ERROR_TEXT, bg: ERROR_BG, border: ERROR_BORDER },
                      { label: t.medium, count: riskMedium, color: '#d97706', bg: '#fffbeb', border: '#fcd34d' },
                      { label: t.low,    count: riskLow,    color: BRAND_PRIMARY, bg: '#f0fdf4', border: '#86efac' },
                    ].map(({ label, count, color, bg, border }) => (
                      <div
                        key={label}
                        className="exec-badge"
                        style={{
                          display: 'flex', alignItems: 'center', gap: '0.4rem',
                          background: bg, border: `1px solid ${border}`,
                          borderRadius: 7, padding: '0.4rem 0.85rem',
                        }}
                      >
                        <span style={{ fontFamily: FONT_FAMILY_DISPLAY, fontWeight: 800, fontSize: '1.5rem', color, lineHeight: 1 }}>
                          {count}
                        </span>
                        <span style={{ ...FONT_TINY, color: TEXT_MUTED }}>
                          {label}
                        </span>
                      </div>
                    ))}
                  </div>
                  <div style={{ ...FONT_LABEL, color: TEXT_MUTED }}>
                    {riskTreated} {t.treatedOrAccepted}
                    <span style={{ color: TEXT_DISABLED }}> / {riskTotal}</span>
                  </div>
                </>
              )
            }
          </div>

          {/* Panel 4 — Compliance coverage */}
          <div className="exec-panel" style={panel}>
            <div style={panelLabel}>{t.complianceTitle}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.1rem' }}>
              <div>
                <div style={subLabel}>{t.controlsImplemented}</div>
                {soa === null ? dash : (
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.45rem' }}>
                    <span style={{ fontFamily: FONT_FAMILY_DISPLAY, fontWeight: 800, fontSize: '1.6rem', color: TEXT_HEADING, lineHeight: 1 }}>
                      {controlsPct !== null ? `${controlsPct}%` : '—'}
                    </span>
                    {soaTotal !== null && soaTotal > 0 && (
                      <span style={{ ...FONT_SMALL, color: TEXT_MUTED }}>
                        {soaImplemented} / {soaTotal} {t.ofControls}
                      </span>
                    )}
                  </div>
                )}
              </div>
              <div>
                <div style={subLabel}>{t.objectivesMet}</div>
                {objectives === null ? dash : (
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.45rem' }}>
                    <span style={{ fontFamily: FONT_FAMILY_DISPLAY, fontWeight: 800, fontSize: '1.6rem', color: TEXT_HEADING, lineHeight: 1 }}>
                      {objectivesPct !== null ? `${objectivesPct}%` : '—'}
                    </span>
                    {objTotal !== null && objTotal > 0 && (
                      <span style={{ ...FONT_SMALL, color: TEXT_MUTED }}>
                        {objMet} / {objTotal} {t.ofObjectives}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

        </div>

        {/* Row 3: Panel 5 + Panel 6 */}
        <div className="exec-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem', marginBottom: '2rem' }}>

          {/* Panel 5 — Audit and review calendar */}
          <div className="exec-panel" style={panel}>
            <div style={panelLabel}>{t.calendarTitle}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.9rem' }}>
              <div>
                <div style={subLabel}>{t.lastInternalAudit}</div>
                <div style={valueLg}>
                  {audits === null ? '—'
                    : lastAuditDate ? fmtDate(lastAuditDate, orgLang)
                    : t.notRecorded}
                </div>
              </div>
              <div>
                <div style={subLabel}>{t.nextPlannedAudit}</div>
                <div style={valueLg}>
                  {audits === null ? '—'
                    : nextPlannedAuditItem ? fmtDate(nextPlannedAuditItem.plannedDate, orgLang)
                    : t.notScheduled}
                </div>
              </div>
              <div style={{ borderTop: '1px solid #e5e3df', paddingTop: '0.9rem' }}>
                <div style={subLabel}>{t.lastMgmtReview}</div>
                <div style={valueLg}>
                  {mgmtReviews === null ? '—'
                    : lastMrDate ? fmtDate(lastMrDate, orgLang)
                    : t.notRecorded}
                </div>
              </div>
              <div>
                <div style={subLabel}>{t.nextMgmtReview}</div>
                <div style={valueLg}>
                  {mgmtReviews === null ? '—'
                    : nextMrDate ? fmtDate(nextMrDate, orgLang)
                    : t.notScheduled}
                </div>
              </div>
            </div>
          </div>

          {/* Panel 6 — Nonconformities */}
          <div className="exec-panel" style={panel}>
            <div style={panelLabel}>{t.ncTitle}</div>
            {nonconformities === null ? dash
              : ncTotal === 0 ? (
                <span style={{ ...FONT_LABEL, color: TEXT_DISABLED }}>{t.noNcs}</span>
              ) : (
                <div style={{ display: 'flex', gap: '1.75rem', flexWrap: 'wrap', alignItems: 'flex-start' }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontFamily: FONT_FAMILY_DISPLAY, fontWeight: 800, fontSize: '2.2rem', color: TEXT_HEADING, lineHeight: 1 }}>
                      {ncOpen}
                    </div>
                    <div style={{ ...FONT_TINY, color: TEXT_MUTED, marginTop: '0.2rem' }}>
                      {t.openNcs}
                    </div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontFamily: FONT_FAMILY_DISPLAY, fontWeight: 800, fontSize: '2.2rem', color: TEXT_DISABLED, lineHeight: 1 }}>
                      {ncClosed}
                    </div>
                    <div style={{ ...FONT_TINY, color: TEXT_MUTED, marginTop: '0.2rem' }}>
                      {t.closedNcs}
                    </div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontFamily: FONT_FAMILY_DISPLAY, fontWeight: 800, fontSize: '2.2rem', color: BRAND_PRIMARY, lineHeight: 1 }}>
                      {ncRate !== null ? `${ncRate}%` : '—'}
                    </div>
                    <div style={{ ...FONT_TINY, color: TEXT_MUTED, marginTop: '0.2rem' }}>
                      {t.closureRate}
                    </div>
                  </div>
                </div>
              )
            }
          </div>

        </div>

      </div>
    </div>
  );
}
